import { Alert, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * Help center topic: standard catalog expansion operation manual.
 * Documents the full workflow (candidate selection -> identity data ->
 * culture data -> AI evaluation -> validation -> publishing) so future
 * expansion stays consistent with the current 500-record core library.
 */
export function CellLineCatalogExpansionHelp() {
  const { i18n } = useTranslation();
  const zh = i18n.resolvedLanguage !== 'en-US';

  const steps = zh
    ? [
        ['选择候选', '按实验室常用清单或领域高频细胞系选录，优先有官方库收录的条目；明确属于 ICLAC 误鉴定/交叉污染清单的细胞系不作为普通条目收录。'],
        ['解析身份字段', '从 Cellosaurus 快照（当前 56.0，2026-06-25）解析名称、别名、物种、细胞类别、组织来源、疾病，并保留原始值、版本与链接。'],
        ['冲突检查', '与全库 500 条做归一化比对：标准编码、CVCL 收录号必须唯一，名称/别名不得跨记录重叠；同源衍生关系记录 RELATED_LINE 提示。'],
        ['生成种子与身份数据', '将新条目写入 cell-line-catalog.seed.json，运行 build:catalog-identity-data 生成身份数据。'],
        ['培养数据', '运行 fetch:catalog-culture-tier-a 抓取 ATCC/DSMZ/ECACC/JCRB/RIKEN 官方页面（Tier A）；页面不可得时按官方惯例整理为 Tier B 并标注“待来源复核”。'],
        ['AI 评估', 'AI 评估器按 cell-line-catalog-ai-standard-v1 逐条检查身份、来源与风险，输出 aiStatus、findings、riskReasons、evidenceSummary 并写入评估记录，不覆盖历史结论。'],
        ['校验与回归', '运行 check:catalog-culture-data、check:catalog-identity-data、check:catalog-gold-standard、check:catalog-seed-sql，并重跑金标准回归。'],
        ['发布与审计', '管理员在标准细胞系列表复核后发布/隐藏/退役，操作写入审计日志；业务用户仅见已发布且未退役的记录。'],
      ]
    : [
        ['Select candidates', 'Choose high-frequency lines used in the lab or the field, preferring entries with official bank records; lines on the ICLAC misidentification/contamination register are not added as ordinary entries.'],
        ['Parse identity fields', 'Parse names, aliases, species, cell category, tissue origin and disease from the Cellosaurus snapshot (currently 56.0, 2026-06-25), keeping raw values, version and links.'],
        ['Conflict check', 'Normalize against all 500 records: codes and CVCL accessions must be unique; names/aliases must not overlap across records; same-donor derivatives get a RELATED_LINE note.'],
        ['Generate seed and identity data', 'Append new entries to cell-line-catalog.seed.json and run build:catalog-identity-data.'],
        ['Culture data', 'Run fetch:catalog-culture-tier-a to scrape official pages (Tier A); when pages are unavailable, record Tier B from official conventions with a “pending source review” marker.'],
        ['AI evaluation', 'The AI evaluator checks identity, sources and risks per cell-line-catalog-ai-standard-v1, writes aiStatus, findings, riskReasons and evidenceSummary as immutable evaluation records.'],
        ['Validation and regression', 'Run check:catalog-culture-data, check:catalog-identity-data, check:catalog-gold-standard and check:catalog-seed-sql, then re-run the gold-standard regression.'],
        ['Publish and audit', 'Administrators review and publish/hide/retire records in the catalog list; every action is audited. Business users only see published, non-retired records.'],
      ];

  const dataFiles = zh
    ? [
        ['cell-line-catalog.seed.json', '标准库种子（编码、CVCL、名称、别名、来源、风险、层级）', '手工/脚本维护，重新生成种子 SQL'],
        ['cell-line-identity-data.json', '身份字段（细胞类别/组织来源/疾病）与来源溯源', 'build:catalog-identity-data 生成'],
        ['cell-line-culture-tier-b.json', 'Tier B 培养数据（待来源复核）', '手工/脚本维护'],
        ['cell-line-culture-tier-a.json', 'Tier A 培养数据（官方页面核实）', 'fetch:catalog-culture-tier-a 生成'],
        ['cell-line-culture-data.json', '培养数据合并文件（Tier A 覆盖同名记录）', 'build:catalog-culture-data 生成'],
        ['cell-line-catalog-ai-evaluated.json', 'AI 评估记录（状态、发现、证据摘要）', 'AI 评估后写入，不覆盖历史'],
        ['cell-line-catalog-evaluation-gold-standard.json', '金标准回归集', '标准/评估变更时更新'],
        ['cell-line-core-expansion-v2.json', '核心库扩充选择清单', '扩充时维护'],
      ]
    : [
        ['cell-line-catalog.seed.json', 'Catalog seed (code, CVCL, names, aliases, source, risk, tier)', 'Maintained by hand/scripts; regenerates the seed SQL'],
        ['cell-line-identity-data.json', 'Identity fields (category/tissue/disease) with provenance', 'Generated by build:catalog-identity-data'],
        ['cell-line-culture-tier-b.json', 'Tier B culture data (pending source review)', 'Maintained by hand/scripts'],
        ['cell-line-culture-tier-a.json', 'Tier A culture data (verified official pages)', 'Generated by fetch:catalog-culture-tier-a'],
        ['cell-line-culture-data.json', 'Merged culture data (Tier A overrides same-code Tier B)', 'Generated by build:catalog-culture-data'],
        ['cell-line-catalog-ai-evaluated.json', 'AI evaluation records (status, findings, evidence)', 'Written by AI evaluation; history is never overwritten'],
        ['cell-line-catalog-evaluation-gold-standard.json', 'Gold-standard regression set', 'Updated when the standard or evaluator changes'],
        ['cell-line-core-expansion-v2.json', 'Core library expansion candidate list', 'Maintained during expansion'],
      ];

  const commands = zh
    ? [
        ['build:catalog-identity-data', '从 Cellosaurus 快照生成身份数据（需 CELLOSAURUS_TXT）'],
        ['fetch:catalog-culture-tier-a', '抓取官方培养页面并生成 Tier A（增量/--refresh/--limit）'],
        ['build:catalog-culture-data', '合并 Tier B 与 Tier A 生成培养数据文件'],
        ['build:catalog-seed-sql', '重新生成部署用种子迁移 0042'],
        ['check:catalog-culture-data', '校验培养数据覆盖、枚举、来源与待复核标记'],
        ['check:catalog-identity-data', '校验身份数据覆盖、枚举与来源完整性'],
        ['check:catalog-gold-standard', '金标准回归（当前 22 条记录用例）'],
        ['check:catalog-seed-sql', '校验种子 SQL 与数据文件一致'],
        ['db:seed-cell-line-catalog', '幂等应用标准库种子到本地数据库'],
      ]
    : [
        ['build:catalog-identity-data', 'Generate identity data from a Cellosaurus snapshot (needs CELLOSAURUS_TXT)'],
        ['fetch:catalog-culture-tier-a', 'Scrape official culture pages and generate Tier A (incremental/--refresh/--limit)'],
        ['build:catalog-culture-data', 'Merge Tier B and Tier A into the culture data file'],
        ['build:catalog-seed-sql', 'Regenerate the deployment seed migration 0042'],
        ['check:catalog-culture-data', 'Validate culture coverage, enums, sources and review markers'],
        ['check:catalog-identity-data', 'Validate identity coverage, enums and provenance'],
        ['check:catalog-gold-standard', 'Gold-standard regression (currently 22 record cases)'],
        ['check:catalog-seed-sql', 'Validate seed SQL is in sync with the data files'],
        ['db:seed-cell-line-catalog', 'Idempotently apply the catalog seed to the local database'],
      ];

  const gating = zh
    ? [
        ['AI_PASSED', '身份、来源、风险证据一致', '允许自动发布'],
        ['AI_WARNING', '存在公开风险、身份存疑或资料缺口', '阻断自动发布，可人工复核后带风险标签发布'],
        ['REVIEW_REQUIRED', '身份冲突、可能重复、来源退役、误鉴定未排除', '阻断发布，需人工复核'],
        ['REJECTED', '确认错误、重复或不应使用', '阻断发布'],
      ]
    : [
        ['AI_PASSED', 'Identity, source and risk evidence are consistent', 'Auto-publish allowed'],
        ['AI_WARNING', 'Public risk, identity doubt or data gaps exist', 'Auto-publish blocked; may publish after human review with a risk label'],
        ['REVIEW_REQUIRED', 'Identity conflict, possible duplicate, retired source, or unresolved misidentification', 'Publish blocked; human review required'],
        ['REJECTED', 'Confirmed error, duplicate or line not recommended for use', 'Publish blocked'],
      ];

  return (
    <>
      <Alert
        type="info"
        showIcon
        message={
          zh
            ? '核心库当前 500 条（已达目标上限），扩展库目标 1,000～3,000 条。任何新增、来源版本升级或数据复核都必须由 AI 评估器按评估标准完成，不允许仅凭代码阈值直接判定通过。'
            : 'The core library currently has 500 records (target ceiling reached) and the extended library targets 1,000–3,000. Any addition, source upgrade or data review must be evaluated by the AI evaluator per the evaluation standard — code thresholds alone cannot decide a pass.'
        }
        style={{ marginBottom: 16 }}
      />

      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {zh ? '扩充流程' : 'Expansion workflow'}
      </Typography.Title>
      <ol style={{ paddingLeft: 20, marginBottom: 16 }}>
        {steps.map(([title, detail]) => (
          <li key={title} style={{ marginBottom: 8 }}>
            <b>{title}</b>：{detail}
          </li>
        ))}
      </ol>

      <Typography.Title level={5}>
        {zh ? '数据文件' : 'Data files'}
      </Typography.Title>
      <Table
        size="small"
        pagination={false}
        rowKey="file"
        dataSource={dataFiles.map(([file, role, maintain]) => ({
          file,
          role,
          maintain,
        }))}
        columns={[
          { title: zh ? '文件' : 'File', dataIndex: 'file', width: 300 },
          { title: zh ? '作用' : 'Purpose', dataIndex: 'role' },
          { title: zh ? '维护方式' : 'Maintenance', dataIndex: 'maintain', width: 280 },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Typography.Title level={5}>
        {zh ? '命令速查' : 'Command reference'}
      </Typography.Title>
      <Table
        size="small"
        pagination={false}
        rowKey="command"
        dataSource={commands.map(([command, purpose]) => ({
          command,
          purpose,
        }))}
        columns={[
          { title: zh ? '命令' : 'Command', dataIndex: 'command', width: 300 },
          { title: zh ? '作用' : 'Purpose', dataIndex: 'purpose' },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Typography.Title level={5}>
        {zh ? '评估状态与发布门禁' : 'Evaluation status and publish gating'}
      </Typography.Title>
      <Table
        size="small"
        pagination={false}
        rowKey="status"
        dataSource={gating.map(([status, condition, gate]) => ({
          status,
          condition,
          gate,
        }))}
        columns={[
          { title: zh ? '状态' : 'Status', dataIndex: 'status', width: 170 },
          { title: zh ? '判定条件' : 'Condition', dataIndex: 'condition' },
          { title: zh ? '发布' : 'Publishing', dataIndex: 'gate', width: 260 },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Typography.Title level={5}>
        {zh ? '注意事项' : 'Notes'}
      </Typography.Title>
      <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
        <li>
          {zh
            ? '高风险、身份冲突、多候选匹配和来源不完整的记录不能自动发布；AI_WARNING 记录人工复核发布时必须保留中文风险标签。'
            : 'High-risk, identity-conflict, multi-candidate and incomplete-source records must not auto-publish; AI_WARNING records published after review must keep the Chinese risk label.'}
        </li>
        <li>
          {zh
            ? '身份字段（类别/组织来源/疾病）与培养字段属于注释性数据，升级不改变 AI 评估状态；缺失字段留空，不猜测、不伪造。'
            : 'Identity fields (category/tissue/disease) and culture fields are annotations; upgrading them does not change AI status. Missing values stay empty — never guessed or fabricated.'}
        </li>
        <li>
          {zh
            ? '“未发现公开风险记录”不等于“安全”“已认证”“已验证”，也不等于实验室实物认证。'
            : '“No public risk records found” does not mean “safe”, “certified” or “verified”, and is not a laboratory authentication.'}
        </li>
        <li>
          {zh
            ? '来源版本升级或标准修订后必须生成新的评估记录，不覆盖历史结论。'
            : 'Source upgrades or standard revisions must create new evaluation records; historical conclusions are never overwritten.'}
        </li>
      </ul>
    </>
  );
}
